import { makeAutoObservable, action, runInAction } from 'mobx';
import * as ExcelJS from 'exceljs';
import { v4 as uuidv4 } from 'uuid';
import { TemplateModel } from '../models/TemplateModel';
import { ViewTypes } from '../models/SheetModel';
import { CellValidationHelper } from '../helpers/CellValidationHelper';

export class WorkbookViewModel {
    view = 'columns';
    isSheetTypeDialogOpen = false;
    isSaving = false;
    isSidebarExpanded = true; // Default to expanded
    isNewFileViewOpen = false; // Default to false - Start with workbook closed

    constructor(stores) {
        this.stores = stores;
        makeAutoObservable(this, {
            stores: false,
            isSaving: true,
            // Make new states observable
            isSidebarExpanded: true,
            isNewFileViewOpen: true,
        });

        // Determine initial state: If no workbook is loaded, open the NewFileView
        if (!this.currentWorkbook) {
            this.isNewFileViewOpen = true;
             // Sidebar should likely be expanded initially if no workbook is open
             this.isSidebarExpanded = true;
        }
    }

    // --- Save Prompt Logic (Basic) ---
    _confirmDiscardChanges = action(() => {
        if (!this.isSaved && this.currentWorkbook) {
            return window.confirm(
                'You have unsaved changes. Are you sure you want to discard them and proceed?'
            );
        }
        return true; // No unsaved changes or no workbook open
    });


    // --- Sidebar and New File View Actions ---
    toggleSidebar = action(() => {
        this.isSidebarExpanded = !this.isSidebarExpanded;
        console.log("Sidebar expanded:", this.isSidebarExpanded);
    });

    openNewFileView = action(() => {
        if (!this._confirmDiscardChanges()) return; // Check for unsaved changes

        console.log("Opening New File View");
        this.isNewFileViewOpen = true;
        // Optionally close the current workbook reference if desired
        // this.stores.workbookStore.closeCurrentWorkbook(); // Add this method to WorkbookStore if needed
    });

    closeNewFileView = action(() => {
        this.isNewFileViewOpen = false;
        console.log("Closing New File View");
    });


    // --- Modified/New File Handling Actions ---

    // Called when "Create Blank" is clicked in the dedicated NewFileView
    handleCreateBlankWorkbook = action(async () => {
        // This version is ONLY for the global "New File" / "Create Blank" button
        console.log('Creating new blank workbook FROM NewFileView.');
        this.closeNewFileView(); // Close the new file view FIRST
        // Clear any selected file in the sidebar
        this.stores.fileSystemStore.clearSelectedFile();
        // *** Crucially, clear any existing file handle ***
        this.stores.fileSystemStore.clearCurrentFileHandle();

        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'Untitled Workbook';
        workbook.id = uuidv4();
        workbook.isNewWorkbook = true;
        workbook.targetDirectoryHandle = null; // Ensure no target dir for global new file

        const sheet = workbook.addWorksheet('Sheet 1');
        sheet.sheetType = 'unknown';

        this.stores.workbookStore.addWorkbook(workbook);
        this.stores.sheetStore.resetSelection();
        this.stores.fileSystemStore.markUnsaved();
    });

    handleSelectTemplate = action(async (templateId) => {
        console.log('Creating workbook from template:', templateId);
        this.closeNewFileView(); // Close the new file view
        
        // Clear any selected file in the sidebar
        this.stores.fileSystemStore.clearSelectedFile();
        // Clear any existing file handle
        this.stores.fileSystemStore.clearCurrentFileHandle();
        
        // Get templates and find the selected one
        const templates = this.stores.templateStore?.templates || 
                          TemplateModel.getTemplates();
        
        const selectedTemplate = templates.find(t => t.id === templateId);
        if (!selectedTemplate) {
            console.error('Template not found:', templateId);
            return;
        }
        
        // Create workbook from template
        const workbook = selectedTemplate.createFn();
        workbook.id = uuidv4();
        workbook.isNewWorkbook = true;
        workbook.targetDirectoryHandle = null;
        
        // Add to store
        this.stores.workbookStore.addWorkbook(workbook);
        this.stores.sheetStore.resetSelection();
        this.stores.fileSystemStore.markUnsaved();
    });

    // Called when "Open File" is clicked in Sidebar
    handleOpenFileFromSidebar = action(async () => {
         if (!this._confirmDiscardChanges()) return; // Check for unsaved changes
         this.closeNewFileView(); // Ensure new file view is closed
         // Clear selection before opening file picker
         this.stores.fileSystemStore.clearSelectedFile();
         await this.handleFileOpen(); // Reuse existing logic
    });

     // Called when "Open Folder" is clicked in Sidebar
     handleRequestDirectoryAccessFromSidebar = action(async () => {
        // No unsaved changes check needed here, typically
        this.closeNewFileView(); // Ensure new file view is closed
        // Clear selection when opening a new folder
        this.stores.fileSystemStore.clearSelectedFile();
        await this.requestDirectoryAccess(); // Reuse existing logic
    });


    checkUserDataPermission = async () => {
        // First check if the browser supports the File System Access API
        const { fileSystemStore } = this.stores;
        const isSupported = fileSystemStore.isFileSystemAccessSupported();
        
        if (!isSupported) {
            console.log('Browser does not support File System Access API, skipping permission check');
            return true; // Return true to avoid showing permission dialog
        }
        
        const hasPermission = localStorage.getItem('userDataPermissionGranted') === 'true';
        
        if (hasPermission) {
            console.log('User has already granted permission to store data');
            return true;
        }
        
        console.log('User has not granted permission to store data yet');
        return false;
    };

    setUserDataPermission = (granted) => {
        if (granted) {
            localStorage.setItem('userDataPermissionGranted', 'true');
            console.log('User granted permission to store data');
        } else {
            localStorage.setItem('userDataPermissionGranted', 'false');
            console.log('User declined permission to store data');
        }
        return granted;
    };

    requestUserDataPermission = async () => {
        // First check if the browser supports the File System Access API
        const { fileSystemStore } = this.stores;
        if (!fileSystemStore.isFileSystemAccessSupported()) {
            console.log('Browser does not support File System Access API, skipping permission request');
            return false;
        }
        
        // Set permission granted
        this.setUserDataPermission(true);
        
        // After getting permission, try to restore directory access
        await fileSystemStore.tryRestoreDirectoryAccess();
        
        return true;
    };

    handleFileOpen = action(async (droppedFile = null) => {
        // Confirmation and selection clearing is handled by the caller
        try {
            let file, handle;

            if (droppedFile) {
                file = droppedFile;
                handle = null; // No handle for dropped files initially
                // Clear selection for dropped files too
                this.stores.fileSystemStore.clearSelectedFile();
            } else {
                 // fileSystemStore.openFile is called, selection cleared by handleOpenFileFromSidebar
                const result = await this.stores.fileSystemStore.openFile();
                if (!result || !result.file) {
                    console.log("File open cancelled by user.");
                    return; // Exit early
                }
                file = result.file;
                handle = result.handle;
                 // Clear selection explicitly IF the file opened is NOT within the current directory
                 // This is hard to check here easily. Let's rely on the caller clearing it.
                 // if (handle && this.stores.fileSystemStore.directoryHandle) {
                 //    // Need a way to check if handle is inside directoryHandle tree
                 // } else {
                 //    this.stores.fileSystemStore.clearSelectedFile();
                 // }
            }

            // File guaranteed here if not cancelled
            const workbook = new ExcelJS.Workbook();
            workbook.isNewWorkbook = false;
            
            if (file.name.toLowerCase().endsWith('.csv')) {
                const text = await file.text();
                const worksheet = workbook.addWorksheet('Sheet 1'); // CSV gets one sheet
                await worksheet.csv.parse(text);
                workbook.isCSV = true;
                workbook.properties.title = file.name.replace(/\.csv$/, '');
                workbook.originalFileName = file.name;
            } else {
                await workbook.xlsx.load(await file.arrayBuffer());
                workbook.originalFileName = file.name;
                
                if (!workbook.properties.title) {
                    workbook.properties.title = file.name.replace(/\.xlsx$/, '');
                }
                // *** APPLY METADATA ***
                // this._applyMetadataToWorkbook(workbook); // Removed
                
                // *** Read Tab Colors from Loaded Workbook ***
                console.log("Reading sheet tab colors...");
                workbook.worksheets.forEach(worksheet => {
                    if (worksheet.properties.tabColor?.argb) {
                        // Store the #RRGGBB on our custom property for easy UI access
                        const argb = worksheet.properties.tabColor.argb;
                        // Ensure it's a valid ARGB string (8 hex chars)
                        if (typeof argb === 'string' && argb.length === 8) {
                            worksheet.color = '#' + argb.substring(2); // Extract RRGGBB
                            console.log(`  - Found color ${worksheet.color} for sheet ${worksheet.name}`);
                        } else {
                            console.warn(`  - Invalid ARGB format found for sheet ${worksheet.name}:`, argb);
                            delete worksheet.color;
                        }
                    } else {
                        delete worksheet.color; // Ensure no stale color property
                    }
                });
                console.log("Finished reading tab colors.");
            }
            
            workbook.id = uuidv4();
            this.stores.workbookStore.addWorkbook(workbook); // This sets the current workbook
            // DEBUG: Check if sheetStore exists before calling resetSelection
            console.log('[WorkbookViewModel] Before resetSelection call:', {
                hasStores: !!this.stores,
                hasSheetStore: !!this.stores?.sheetStore,
                sheetStoreType: typeof this.stores?.sheetStore,
                hasResetMethod: typeof this.stores?.sheetStore?.resetSelection === 'function'
            });
            this.stores.sheetStore.resetSelection();
            this.stores.fileSystemStore.setCurrentFileHandle(handle);

             // Successfully opened, ensure view is closed and sidebar potentially collapses?
             // Closing view is already handled by caller.
             runInAction(() => {
                this.isNewFileViewOpen = false;
                // Optional: Collapse sidebar after opening file?
                // this.isSidebarExpanded = false;
             });

        } catch (error) {
            console.error('Error opening workbook:', error);
            alert(`Error opening workbook: ${error.message}`); // Basic user feedback
        }
    });

    get currentWorkbook() {
        return this.stores.workbookStore.currentWorkbook;
    }

    get isSaved() {
        // Check if fileSystemStore exists before accessing its property
        return this.stores?.fileSystemStore?.isSaved ?? true;
    }

    get hasEditPermission() {
        // Check if fileSystemStore exists before accessing its property
        return this.stores?.fileSystemStore?.hasEditPermission ?? true;
    }

    setView = action((newView) => {
        this.view = newView;
    });

    openSheetTypeDialog = action(() => {
        // Ensure a sheet is actually selected before opening
        if (this.stores.sheetStore?.currentSheet) {
            this.isSheetTypeDialogOpen = true;
        } else {
            console.warn("Attempted to open SheetTypeDialog with no active sheet.");
        }
    });

    closeSheetTypeDialog = action(() => {
        this.isSheetTypeDialogOpen = false;
    });

    handleSave = action(async () => {
        if (!this.currentWorkbook || this.isSaving) return;

        console.log("ViewModel: Initiating save...");
        const originalSheetName = this.stores.sheetStore.currentSheet?.name;
        let success = false;

        try {
            runInAction(() => { this.isSaving = true; });

            // --- Sync ALL sheets --- 
            // We need to process all sheets to ensure styles/CF from templates are correctly applied
            const allVisibleSheets = this.stores.sheetStore.visibleSheets.map(s => s.name);
            console.log("ViewModel: Syncing ALL visible sheets:", allVisibleSheets);
            for (const sheetName of allVisibleSheets) {
                console.log(`ViewModel: Loading and saving sheet: ${sheetName}`);
                // Load sheet data into SheetDataStore
                await this.stores.sheetDataStore.loadSheet(sheetName, { validateOutlineLevels: false });
                // Save changes from SheetDataStore model back to ExcelJS object (applies styles/CF)
                await this.stores.sheetDataStore.saveChanges();
            }
            console.log("ViewModel: Finished syncing ALL visible sheets.");
            // --- End Sync ---

            if (originalSheetName && this.stores.sheetDataStore.currentSheet?.name !== originalSheetName) {
                 console.log(`ViewModel: Restoring active sheet context in SheetDataStore to: ${originalSheetName}`);
                 await this.stores.sheetDataStore.loadSheet(originalSheetName, { validateOutlineLevels: false });
            }

            const workbookToSave = this.currentWorkbook;
            if (!workbookToSave) {
                 throw new Error("Current workbook became unavailable during save process.");
            }

             // --- Log the state right before calling saveWorkbook ---
             console.log('ViewModel: Preparing to call fileSystemStore.saveWorkbook. Workbook State:', {
                 isNew: workbookToSave.isNewWorkbook,
                 title: workbookToSave.properties.title,
                 originalFileName: workbookToSave.originalFileName,
                 hasTargetDirectoryHandle: !!workbookToSave.targetDirectoryHandle,
                 targetDirectoryName: workbookToSave.targetDirectoryHandle?.name
             });
             // --- End Logging ---

            // --- Determine Suggested File Name ---
            let suggestedFileName;
            // If it has an original name AND is NOT marked as new, use original.
            // Otherwise (new file OR loaded file being saved 'as'), use title.
            if (workbookToSave.originalFileName && !workbookToSave.isNewWorkbook) {
                suggestedFileName = workbookToSave.originalFileName;
            } else {
                const formattedTitle = this.formatFileName(workbookToSave.properties.title || 'Untitled Workbook');
                suggestedFileName = `${formattedTitle}.xlsx`;
                 // Update title property only if it was untitled and different from formatted
                 if (workbookToSave.properties.title === 'Untitled Workbook' && formattedTitle !== 'Untitled Workbook') {
                     runInAction(() => { workbookToSave.properties.title = formattedTitle; });
                 } else if (workbookToSave.properties.title !== formattedTitle && !workbookToSave.originalFileName) {
                      // Update title if it changed due to formatting (and wasn't loaded)
                      runInAction(() => { workbookToSave.properties.title = formattedTitle; });
                 }
            }
            console.log(`ViewModel: Calling fileSystemStore.saveWorkbook with suggested name: ${suggestedFileName}`);

            // --- Call Store Save Method ---
            const saveResult = await this.stores.fileSystemStore.saveWorkbook(
                workbookToSave,
                suggestedFileName
            );
            console.log("ViewModel: fileSystemStore.saveWorkbook() completed.", saveResult);

            // --- Handle Save Result ---
            if (saveResult?.success) {
                success = true;
                 // Update workbook state after successful save
                 runInAction(() => {
                    workbookToSave.isNewWorkbook = false; // No longer 'new' after any successful save
                     // Update originalFileName with the name actually used for saving
                     if (saveResult.fileName) {
                         workbookToSave.originalFileName = saveResult.fileName;
                         // Optional: Update title to match filename if it was untitled
                         // Or maybe only if it was 'Untitled Workbook'? User might have set title intentionally.
                         const nameWithoutExt = saveResult.fileName.replace(/\.xlsx$/i, '');
                         if (workbookToSave.properties.title === 'Untitled Workbook' || workbookToSave.properties.title !== nameWithoutExt) {
                            console.log(`Updating workbook title to match saved file name: ${nameWithoutExt}`);
                            workbookToSave.properties.title = nameWithoutExt;
                         }
                     }
                     // FileSystemStore now manages the handle internally
                     // this.stores.fileSystemStore.setCurrentFileHandle(saveResult.handle); // Not needed
                 });
            } else if (saveResult?.error === 'cancelled') {
                // User cancelled the save dialog
                console.log("ViewModel: Save cancelled by user.");
                success = false; // Ensure success is false
            } else {
                 // Handle other failures
                 success = false;
                 console.error("ViewModel: fileSystemStore.saveWorkbook indicated failure:", saveResult?.error);
                 // Optionally alert the user about the error saveResult?.error
            }

        } catch (error) {
            console.error('ViewModel: Error during save process:', error);
            success = false;
             // alert(`Error saving workbook: ${error.message}`);
        } finally {
            if (success) {
                this.stores.sheetDataStore.clearModifiedFlags();
                // FileSystemStore handles its own isSaved state
            }
            runInAction(() => { this.isSaving = false; });
            console.log("ViewModel: Save process finished. Success:", success);
        }
    });

    handleSaveAs = action(async () => {
        if (!this.currentWorkbook || this.isSaving) return;

        console.log("ViewModel: Initiating save as...");
        const originalSheetName = this.stores.sheetStore.currentSheet?.name;
        let success = false;

        try {
             runInAction(() => { this.isSaving = true; });

             // --- Sync ALL sheets for Save As --- 
             // Process all sheets to ensure template styles/CF are applied correctly before saving
             const allVisibleSheets = this.stores.sheetStore.visibleSheets.map(s => s.name);
             console.log("ViewModel: Syncing ALL visible sheets for Save As:", allVisibleSheets);
             for (const sheetName of allVisibleSheets) {
                  console.log(`ViewModel: Loading and saving sheet: ${sheetName}`);
                  await this.stores.sheetDataStore.loadSheet(sheetName, { validateOutlineLevels: false });
                  await this.stores.sheetDataStore.saveChanges();
              }
              console.log("ViewModel: Finished syncing ALL visible sheets for Save As.");
              // --- End Sync ---

              // Restore original active sheet context in SheetDataStore if it changed
              if (originalSheetName && this.stores.sheetDataStore.currentSheet?.name !== originalSheetName) {
                   console.log(`ViewModel: Restoring active sheet context in SheetDataStore to: ${originalSheetName}`);
                   await this.stores.sheetDataStore.loadSheet(originalSheetName, { validateOutlineLevels: false });
              }

             const workbookToSave = this.currentWorkbook;
              if (!workbookToSave) {
                  throw new Error("Current workbook became unavailable before Save As dialog.");
             }

             // --- Determine Suggested File Name for Save As ---
             // Always suggest based on the current title property for "Save As"
             const formattedTitle = this.formatFileName(workbookToSave.properties.title || 'Untitled Workbook');
             let suggestedFileName = `${formattedTitle}.xlsx`;
             console.log(`ViewModel: Calling fileSystemStore.saveWorkbookAs with suggested name: ${suggestedFileName}`);

             // --- Call Store Save As Method ---
             // Use the dedicated saveWorkbookAs method
             const saveResult = await this.stores.fileSystemStore.saveWorkbookAs(
                 workbookToSave,
                 suggestedFileName
             );
              console.log("ViewModel: fileSystemStore.saveWorkbookAs() completed.", saveResult);

             // --- Handle Save As Result ---
            if (saveResult?.success) {
                 success = true;
                  // Update workbook state after successful save as
                 runInAction(() => {
                     workbookToSave.isNewWorkbook = false; // Saved, so no longer 'new'
                      // Update originalFileName and title based on what was actually saved
                      if (saveResult.fileName) {
                          workbookToSave.originalFileName = saveResult.fileName;
                          // Update title to match the saved file name (without extension)
                           const nameWithoutExt = saveResult.fileName.replace(/\.xlsx$/i, '');
                           console.log(`Updating workbook title to match Save As file name: ${nameWithoutExt}`);
                           workbookToSave.properties.title = nameWithoutExt;
                      }
                      // FileSystemStore manages the handle internally
                      // this.stores.fileSystemStore.setCurrentFileHandle(saveResult.handle); // Not needed
                 });
            } else if (saveResult?.error === 'cancelled') {
                // User cancelled the save dialog
                console.log("ViewModel: Save As cancelled by user.");
                success = false; // Ensure success is false
            } else {
                 // Handle other failures
                 success = false;
                 console.log("ViewModel: fileSystemStore.saveWorkbookAs indicated failure:", saveResult?.error);
                 // Optionally alert the user about the error saveResult?.error
            }

        } catch (error) {
            console.error('ViewModel: Error saving workbook as:', error);
            success = false;
             // alert(`Error saving workbook as: ${error.message}`);
        } finally {
             if (success) {
                 this.stores.sheetDataStore.clearModifiedFlags();
                 // FileSystemStore handles its own isSaved state
             }
             runInAction(() => { this.isSaving = false; });
             console.log("ViewModel: Save As process finished. Success:", success);
        }
    });

    // Renaming should only affect the 'title' property, which is used for display
    // and as a suggestion for saving new files. It doesn't rename the actual file
    // until a save operation occurs.
    updateWorkbookName = action((newName) => {
        if (!this.currentWorkbook || !newName) return;

        // Allow renaming anytime, but saving behavior depends on isNewWorkbook/originalFileName
        // if (!this.currentWorkbook.isNewWorkbook) {
        //     console.warn('Renaming an existing/uploaded workbook via title property. File name changes on Save/Save As.');
        // }

        try {
            // Format the name for display and potential future saving
            const formattedName = this.formatFileName(newName);
            if (formattedName === this.currentWorkbook.properties.title) return; // No change

            console.log('Updating workbook title:', { oldName: this.currentWorkbook.properties.title, newName: formattedName });
            
            runInAction(() => {
                this.currentWorkbook.properties.title = formattedName;
                // Mark unsaved because the intended file name upon saving might have changed
                if (this.stores?.fileSystemStore) {
                    this.stores.fileSystemStore.markUnsaved();
                }
            });
        } catch (error) {
            console.error('Error updating workbook title:', error);
        }
    });


    handleSaveAsCSV = action(async () => {
        // Use sheetStore to get the currently *selected* sheet in the UI
        const currentSheet = this.stores.sheetStore.currentSheet;
        // Check workbook existence as well
        if (!this.currentWorkbook || !currentSheet || this.isSaving) {
            console.warn("Save As CSV aborted: Missing workbook, sheet, or already saving.", {
                hasWorkbook: !!this.currentWorkbook,
                hasSheet: !!currentSheet,
                isSaving: this.isSaving,
            });
            return;
        }


        console.log("ViewModel: Initiating save as CSV...");
        let success = false;
        try {
            runInAction(() => { this.isSaving = true; });

            // --- Sync changes for the *current* sheet before saving ---
            // Ensure the data in ExcelJS matches the grid state
             console.log(`ViewModel: Syncing current sheet for CSV Export: ${currentSheet.name}`);
             // Load the current sheet into sheetDataStore if it's not already the context
             if (this.stores.sheetDataStore.currentSheet?.name !== currentSheet.name) {
                 await this.stores.sheetDataStore.loadSheet(currentSheet.name, { validateOutlineLevels: false });
             }
             // Save changes from the grid to the ExcelJS sheet object
             await this.stores.sheetDataStore.saveChanges();
             console.log("ViewModel: Finished syncing current sheet for CSV Export.");
             // --- End Sync ---

            // Fetch the potentially updated sheet object after saving changes
             const sheetToSave = this.currentWorkbook.getWorksheet(currentSheet.id); // Use ID for safety
             if (!sheetToSave) {
                 throw new Error(`Sheet with ID ${currentSheet.id} (${currentSheet.name}) not found in workbook after sync.`);
             }


            // Suggest name based on workbook title + sheet name
            const wbFormattedTitle = this.formatFileName(this.currentWorkbook.properties.title || 'workbook');
            const sheetFormattedName = this.formatFileName(sheetToSave.name || 'Sheet');
            const suggestedName = `${wbFormattedTitle}-${sheetFormattedName}.csv`;

            console.log(`ViewModel: Calling fileSystemStore.saveWorkbookAsCSV for sheet: ${sheetToSave.name}, suggested name: ${suggestedName}`);
            // Pass the actual ExcelJS worksheet object
            await this.stores.fileSystemStore.saveWorkbookAsCSV(
                sheetToSave,
                suggestedName
            );
            console.log("ViewModel: fileSystemStore.saveWorkbookAsCSV() completed.");
            success = true;

        } catch (error) {
            console.error('ViewModel: Error saving workbook as CSV:', error);
             // alert(`Error saving as CSV: ${error.message}`);
            success = false;
        } finally {
            // No need to clear modified flags for CSV export? Or maybe?
            // Let's assume CSV export doesn't affect the main workbook's saved state.
             runInAction(() => { this.isSaving = false; });
             console.log("ViewModel: Save As CSV process finished. Success:", success);
        }
    });

    formatFileName = (name) => {
        // Allow spaces, but trim leading/trailing and collapse multiple spaces
        let formatted = name.replace(/[<>:"/\\|?*\x00-\x1F]/g, ''); // Remove invalid characters
        formatted = formatted.replace(/\s+/g, ' ').trim(); // Collapse and trim whitespace
        if (!formatted) {
            // Provide a default if the name becomes empty after formatting
            return 'Untitled'; // Or 'Untitled Workbook'/'Untitled Sheet' depending on context
        }
        // Enforce a reasonable length limit
        formatted = formatted.slice(0, 100); // Max length 100 chars
        return formatted;
    };

    handleOpenRecentFile = action(async (fileInfo) => {
         if (!this._confirmDiscardChanges()) return; // Check for unsaved changes

        this.closeNewFileView(); // Ensure new file view is closed
         // Clear selection happens within fileSystemStore.openRecentFile
        try {
            if (!fileInfo) {
                throw new Error('Invalid file information');
            }

             // fileSystemStore.openRecentFile now handles clearing/setting selection
            const result = await this.stores.fileSystemStore.openRecentFile(fileInfo);
             // Handle cases where permission might be denied or file not found
            if (!result) {
                 console.log("Could not open recent file, possibly due to permission or file missing.");
                 return;
            }

            const { file, handle } = result;

            // Proceed with loading the workbook data
            const workbook = new ExcelJS.Workbook();
            workbook.isNewWorkbook = false;
            
            if (file.name.toLowerCase().endsWith('.csv')) {
                const text = await file.text();
                const worksheet = workbook.addWorksheet('Sheet 1');
                await worksheet.csv.parse(text);
                workbook.isCSV = true;
                workbook.properties.title = file.name.replace(/\.csv$/, '');
                workbook.originalFileName = file.name;
            } else {
                await workbook.xlsx.load(await file.arrayBuffer());
                workbook.originalFileName = file.name;
                
                if (!workbook.properties.title) {
                    workbook.properties.title = file.name.replace(/\.xlsx$/, '');
                }
                // *** APPLY METADATA ***
                // this._applyMetadataToWorkbook(workbook); // Removed
                
                // *** Read Tab Colors from Loaded Workbook ***
                console.log("Reading sheet tab colors...");
                workbook.worksheets.forEach(worksheet => {
                    if (worksheet.properties.tabColor?.argb) {
                        // Store the #RRGGBB on our custom property for easy UI access
                        const argb = worksheet.properties.tabColor.argb;
                        // Ensure it's a valid ARGB string (8 hex chars)
                        if (typeof argb === 'string' && argb.length === 8) {
                            worksheet.color = '#' + argb.substring(2); // Extract RRGGBB
                            console.log(`  - Found color ${worksheet.color} for sheet ${worksheet.name}`);
                        } else {
                            console.warn(`  - Invalid ARGB format found for sheet ${worksheet.name}:`, argb);
                            delete worksheet.color;
                        }
                    } else {
                        delete worksheet.color; // Ensure no stale color property
                    }
                });
                console.log("Finished reading tab colors.");
            }
            
            workbook.id = uuidv4();
            this.stores.workbookStore.addWorkbook(workbook);
            this.stores.fileSystemStore.setCurrentFileHandle(handle);

             // Successfully opened, ensure view is closed
             runInAction(() => {
                this.isNewFileViewOpen = false;
                 // Optional: Collapse sidebar?
                 // this.isSidebarExpanded = false;
             });

        } catch (error) {
            console.error('Error opening recent file:', error);
             // Ensure selection is cleared on error
             this.stores.fileSystemStore.clearSelectedFile();
            // alert(`Error opening recent file: ${error.message}`);
        }
    });

    requestDirectoryAccess = action(async () => {
        // This just requests access, doesn't open a file, so no unsaved changes check needed
        try {
            const dirHandle = await this.stores.fileSystemStore.requestDirectoryAccess();
            if (dirHandle) {
                console.log('Directory access granted:', dirHandle.name);
                 // Maybe expand sidebar if collapsed to show the new directory view?
                 runInAction(() => {
                     if (!this.isSidebarExpanded) this.isSidebarExpanded = true;
                 });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error requesting directory access:', error);
             // alert(`Error requesting directory access: ${error.message}`);
            return false;
        }
    });

    navigateToDirectory = action(async (dirEntry) => {
        // Navigation doesn't change the workbook state
        try {
            return await this.stores.fileSystemStore.navigateToDirectory(dirEntry);
        } catch (error) {
            console.error('Error navigating to directory:', error);
            return false;
        }
    });

    navigateUp = action(async () => {
         // Navigation doesn't change the workbook state
        try {
            return await this.stores.fileSystemStore.navigateUp();
        } catch (error) {
            console.error('Error navigating up:', error);
            return false;
        }
    });

    openFileFromDirectory = action(async (fileEntry) => {
        if (!this._confirmDiscardChanges()) return; // Check for unsaved changes

        this.closeNewFileView(); // Ensure new file view is closed
        // Selection is handled within fileSystemStore.openFileFromDirectory
        try {
            // fileSystemStore handles getting the file handle/object and setting selection
            const result = await this.stores.fileSystemStore.openFileFromDirectory(fileEntry);
            if (!result) {
                 console.log("Could not open file from directory.");
                 return; // Permission denied or other issue
            }

            const { file, handle } = result;

            // Load workbook data
            const workbook = new ExcelJS.Workbook();
            workbook.isNewWorkbook = false;
            
            if (file.name.toLowerCase().endsWith('.csv')) {
                const text = await file.text();
                const worksheet = workbook.addWorksheet('Sheet 1');
                await worksheet.csv.parse(text);
                workbook.isCSV = true;
                workbook.properties.title = file.name.replace(/\.csv$/, '');
                workbook.originalFileName = file.name;
            } else {
                await workbook.xlsx.load(await file.arrayBuffer());
                workbook.originalFileName = file.name;
                
                if (!workbook.properties.title) {
                    workbook.properties.title = file.name.replace(/\.xlsx$/, '');
                }
                // *** APPLY METADATA ***
                // this._applyMetadataToWorkbook(workbook); // Removed
                
                // *** Read Tab Colors from Loaded Workbook ***
                console.log("Reading sheet tab colors...");
                workbook.worksheets.forEach(worksheet => {
                    if (worksheet.properties.tabColor?.argb) {
                        // Store the #RRGGBB on our custom property for easy UI access
                        const argb = worksheet.properties.tabColor.argb;
                        // Ensure it's a valid ARGB string (8 hex chars)
                        if (typeof argb === 'string' && argb.length === 8) {
                            worksheet.color = '#' + argb.substring(2); // Extract RRGGBB
                            console.log(`  - Found color ${worksheet.color} for sheet ${worksheet.name}`);
                        } else {
                            console.warn(`  - Invalid ARGB format found for sheet ${worksheet.name}:`, argb);
                            delete worksheet.color;
                        }
                    } else {
                        delete worksheet.color; // Ensure no stale color property
                    }
                });
                console.log("Finished reading tab colors.");
            }
            
            workbook.id = uuidv4();
            this.stores.workbookStore.addWorkbook(workbook);
            // DEBUG: Check if sheetStore exists before calling resetSelection
            console.log('[WorkbookViewModel:openFileFromDirectory] Before resetSelection call:', {
                hasStores: !!this.stores,
                hasSheetStore: !!this.stores?.sheetStore,
                sheetStoreType: typeof this.stores?.sheetStore,
                hasResetMethod: typeof this.stores?.sheetStore?.resetSelection === 'function'
            });
            this.stores.sheetStore.resetSelection(); // Reset sheet selection
            this.stores.fileSystemStore.setCurrentFileHandle(handle);

             // Success
             runInAction(() => {
                this.isNewFileViewOpen = false;
                 // Optional: Collapse sidebar?
                 // this.isSidebarExpanded = false;
             });
        } catch (error) {
            console.error('Error opening file from directory:', error);
            // Ensure selection is cleared on error
            this.stores.fileSystemStore.clearSelectedFile();
            // alert(`Error opening file from directory: ${error.message}`);
        }
    });

    // --- METHOD TO MODIFY ---
    // Called ONLY by the '+' button in DirectoryNode
    handleCreateFileInDirectory = action(async (directoryNode) => {
        if (!directoryNode || directoryNode.kind !== 'directory' || !directoryNode.handle) {
            console.error("Invalid directory node provided for file creation:", directoryNode);
            return;
        }
        if (!this._confirmDiscardChanges()) return; // Check for unsaved changes before creating

        console.log(`ViewModel: DIRECTLY Creating new file targeted for directory: ${directoryNode.path.join('/')}`);

        // Clear any selected file in the sidebar
        this.stores.fileSystemStore.clearSelectedFile();
         // *** Crucially, clear any existing file handle ***
         // This new file doesn't represent the previously selected file, even if it has a target dir.
        this.stores.fileSystemStore.clearCurrentFileHandle();

        // Create Workbook
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'Untitled Workbook'; // Default title
        workbook.id = uuidv4();
        workbook.isNewWorkbook = true;
        // *** Set the target directory handle ***
        workbook.targetDirectoryHandle = directoryNode.handle;

        // Add Sheet
        const sheet = workbook.addWorksheet('Sheet 1');
        sheet.sheetType = 'unknown'; // Or your default

        // Add to store and mark unsaved
        this.stores.workbookStore.addWorkbook(workbook); // This switches view to the new workbook
        this.stores.sheetStore.resetSelection();
        this.stores.fileSystemStore.markUnsaved(); // Mark as needing save

        // Ensure the main view is visible
        runInAction(() => {
             if (this.isNewFileViewOpen) {
                 console.warn("NewFileView was open during CreateFileInDirectory, closing it.");
                 this.isNewFileViewOpen = false;
             }
        });
    });

    handleCreateWorkbookFromViewType = action(async (viewId) => {
        console.log(`Creating new workbook FROM View Type: ${viewId}`);
        this.closeNewFileView(); // Close the new file view
        this.stores.fileSystemStore.clearSelectedFile(); // Clear FS selection
        this.stores.fileSystemStore.clearCurrentFileHandle(); // Clear file handle

        try {
            const viewType = ViewTypes[viewId];
            if (!viewType) {
                throw new Error(`Invalid view type ID: ${viewId}`);
            }

            // Create Workbook
            const workbook = new ExcelJS.Workbook();
            workbook.id = uuidv4();
            workbook.isNewWorkbook = true;
            workbook.targetDirectoryHandle = null;

            // Create Sheet with View Name
            const sheetName = `${viewType.icon} ${viewType.label}`;
            // Basic uniqueness check for the initial sheet
            let finalSheetName = sheetName;
            let counter = 1;
            while (workbook.getWorksheet(finalSheetName)) {
                 finalSheetName = `${sheetName} (${counter++})`;
            }
            workbook.properties.title = finalSheetName; // Set workbook title too
            const sheet = workbook.addWorksheet(finalSheetName);
            sheet.sheetType = viewId; // Set sheet type based on view

            // Add Required Columns
            if (viewType.requiredColumns && viewType.requiredColumns.length > 0) {
                 console.log("Adding required columns:", viewType.requiredColumns);
                 const headerRow = sheet.getRow(1);
                 let nextColIndex = 1;
                 viewType.requiredColumns.forEach(colInfo => {
                      const newColIndex = nextColIndex++;
                      const newHeading = colInfo.name;

                      // Add header
                      headerRow.getCell(newColIndex).value = newHeading;
                      const newExcelCol = sheet.getColumn(newColIndex);
                      newExcelCol.header = newHeading;
                      newExcelCol.key = newHeading;
                      newExcelCol.width = 15; // Default width

                      // Apply validation
                      CellValidationHelper.setupNewColumnValidation(sheet, newColIndex, colInfo.type);
                      if (colInfo.type.hasCustomValidation && colInfo.type.defaultOptions) {
                          CellValidationHelper.updateColumnOptions(sheet, newColIndex, colInfo.type.defaultOptions, colInfo.type);
                      }
                 });
                 // Adjust sheet.columns if needed after adding
                 sheet.columns = sheet.columns; // Trigger potential update
            }

            // Add to store & Reset Selection
            this.stores.workbookStore.addWorkbook(workbook);
            this.stores.sheetStore.resetSelection();
            this.stores.fileSystemStore.markUnsaved();

        } catch (error) {
             console.error("Error creating workbook from view type:", error);
             alert(`Failed to create workbook: ${error.message}`); // User feedback
        }
    });
}   